import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import type {AiSearchTableEntityType} from 'analytics/aiSearchAnalytics';
import {useAiSearchTableAnalytics} from 'components/ai/aiSearchTableAnalytics';
import {Avatar} from 'components/DSYS/Avatar';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {
  getComponentsAndServiceReferenceRoute,
  getComponentsAndServicesServiceRoute,
  getTeamsAppsRoute,
  ROUTE_PATHS,
} from 'constant';

import {ColumnSpec} from './aiQueryColumns';
import {
  cellValueToText,
  PersonRefRow,
  readField,
  TeamRefRow,
  toTrimmedString,
  WorkstreamRefRow,
} from './aiQueryCsv';

/** Avatar + name linking to a person's profile; falls back to plain text when there's no ldap to link to. */
const PersonLink = ({person, columnKind}: {person: PersonRefRow; columnKind: string}) => {
  const {onLinkClick} = useAiSearchTableAnalytics();
  const name = toTrimmedString(person.name);
  const ldap = toTrimmedString(person.ldap);
  const label = name || ldap || toTrimmedString(person.email) || toTrimmedString(person.user_id);
  if (!label) {
    return null;
  }
  if (!ldap) {
    return <Text size="small">{label}</Text>;
  }
  const path = `/people/${ldap}`;
  return (
    <ButtonLink
      to={path}
      variant="borderless"
      size="small"
      openInNewTab
      hasNoUnderline
      withIconStart={<Avatar size="small" user={{name: name || ldap, ldap}} />}
      onClick={() => onLinkClick?.({entityType: 'person', columnKind, path})}
    >
      <Text size="small">{label}</Text>
    </ButtonLink>
  );
};

/** An entity name linking to `path`; plain text when there's no page to link to. */
const NameLink = ({
  name,
  path,
  entityType,
  columnKind,
}: {
  name: string;
  path: string | null;
  entityType: AiSearchTableEntityType;
  columnKind: string;
}) => {
  const {onLinkClick} = useAiSearchTableAnalytics();
  if (!name) {
    return null;
  }
  if (!path) {
    return <Text size="small">{name}</Text>;
  }
  return (
    <Text size="small">
      <Link
        to={path}
        openInNewTab
        monochromatic
        hasNoUnderline
        showUnderlineOnHover
        onClick={() => onLinkClick?.({entityType, columnKind, path})}
      >
        {name}
      </Link>
    </Text>
  );
};

/** Workstream name linking to its detail page; falls back to plain text without an id. */
const WorkstreamLink = ({
  workstream,
  columnKind,
}: {
  workstream: WorkstreamRefRow;
  columnKind: string;
}) => {
  const id = workstream.workstream_id;
  return (
    <NameLink
      name={toTrimmedString(workstream.name) || (id != null ? `#${id}` : '')}
      path={id == null ? null : ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(id))}
      entityType="workstream"
      columnKind={columnKind}
    />
  );
};

/** Team name linking to its page; falls back to plain text without a slug. */
const TeamLink = ({team, columnKind}: {team: TeamRefRow; columnKind: string}) => {
  const slug = toTrimmedString(team.slug);
  return (
    <NameLink
      name={toTrimmedString(team.name) || toTrimmedString(team.team_id)}
      path={slug ? ROUTE_PATHS.TEAM.replace(':slug', slug) : null}
      entityType="team"
      columnKind={columnKind}
    />
  );
};

const toId = (value: unknown): number | null => {
  const id = typeof value === 'string' ? Number(value) : value;
  return typeof id === 'number' && Number.isInteger(id) ? id : null;
};

/**
 * Where a DCD service lives in Teams: ServiceNow services are Apps; rSERVER
 * services sit under their component, so one without a component has no page.
 */
export const dcdServicePath = (
  serviceId: number | null,
  source: unknown,
  componentId: number | null
): string | null => {
  if (serviceId == null) {
    return null;
  }
  if (source === 'servicenow') {
    return getTeamsAppsRoute(serviceId);
  }
  return componentId == null ? null : getComponentsAndServicesServiceRoute(componentId, serviceId);
};

/**
 * Render a single result cell richly based on its projected column `kind`:
 * status chips, person profile links, and workstream, team, service, and component links. Plain kinds
 * (text/date/datetime/boolean) reuse the shared text flattener.
 */
export const RenderCell = ({row, column}: {row: Record<string, unknown>; column: ColumnSpec}) => {
  switch (column.kind) {
    case 'status': {
      const value = readField(row, column.field);
      const status = value == null || value === '' ? '' : String(value);
      // Wrapper absorbs the cell's `flex: 1`, so the chip keeps its fit-content width.
      return status ? (
        <Box>
          <StatusChip status={status} />
        </Box>
      ) : null;
    }
    case 'person':
      return (
        <PersonLink
          columnKind={column.kind}
          person={{
            name: readField(row, column.nameField) as string | null | undefined,
            ldap: column.ldapField
              ? (readField(row, column.ldapField) as string | null | undefined)
              : undefined,
          }}
        />
      );
    case 'personRef': {
      const person = readField(row, column.field) as PersonRefRow | null | undefined;
      return person ? <PersonLink columnKind={column.kind} person={person} /> : null;
    }
    case 'personRow':
      return (
        <PersonLink
          columnKind={column.kind}
          person={{
            user_id: row.user_id as string | undefined,
            ldap: row.ldap as string | null | undefined,
            name: row.name as string | null | undefined,
            email: row.email as string | null | undefined,
          }}
        />
      );
    case 'personRefs': {
      const raw = readField(row, column.field);
      if (!Array.isArray(raw) || raw.length === 0) {
        return null;
      }
      return (
        <Box display="flex" flexDirection="column" style={{gap: 4}}>
          {(raw as PersonRefRow[]).map((person, i) => (
            <PersonLink
              key={person.ldap ?? person.user_id ?? i}
              columnKind={column.kind}
              person={person}
            />
          ))}
        </Box>
      );
    }
    case 'workstreamRef': {
      const workstream = readField(row, column.field) as WorkstreamRefRow | null | undefined;
      return workstream ? (
        <WorkstreamLink columnKind={column.kind} workstream={workstream} />
      ) : null;
    }
    case 'workstream':
      return (
        <WorkstreamLink
          columnKind={column.kind}
          workstream={{
            workstream_id: readField(row, column.idField) as number | undefined,
            name: readField(row, column.nameField) as string | null | undefined,
          }}
        />
      );
    case 'teamRef': {
      const team = readField(row, column.field) as TeamRefRow | null | undefined;
      return team ? <TeamLink columnKind={column.kind} team={team} /> : null;
    }
    case 'team':
      return (
        <TeamLink
          columnKind={column.kind}
          team={{
            team_id: readField(row, column.idField) as string | undefined,
            name: readField(row, column.nameField) as string | null | undefined,
            slug: column.slugField
              ? (readField(row, column.slugField) as string | null | undefined)
              : undefined,
          }}
        />
      );
    case 'service':
      return (
        <NameLink
          name={cellValueToText(row, column)}
          path={dcdServicePath(
            toId(readField(row, column.idField)),
            readField(row, column.sourceField),
            toId(readField(row, column.componentIdField))
          )}
          entityType="service"
          columnKind={column.kind}
        />
      );
    case 'component': {
      const id = toId(readField(row, column.idField));
      return (
        <NameLink
          name={cellValueToText(row, column)}
          path={id == null ? null : getComponentsAndServiceReferenceRoute(id)}
          entityType="component"
          columnKind={column.kind}
        />
      );
    }
    default:
      return <Text size="small">{cellValueToText(row, column, {humanizeDates: true})}</Text>;
  }
};
